import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BROWSER_COLORS, FaviconStack, Icon, Skeleton, TabRow, Tag } from '@vault/ui';
import type { Item, Sweep } from '@vault/shared';
import { api } from '../../api/client.js';
import { deriveBrowser, domainOf, fallbackTitle, formatTime, seedLetters } from './helpers.js';

const PREVIEW = 6;

export interface SweepCardProps {
  sweep: Sweep;
  expanded: boolean;
  onToggle: () => void;
  onOpenItem: (item: Item) => void;
  onRequestDelete: () => void;
  /** When provided, "Restore all" routes through the app's confirm→open→undo
   *  flow instead of the raw open-every-tab fallback. */
  onRestore?: () => void;
}

/**
 * One saved session (sweep). Collapsed = favicon stack + title + meta + Restore
 * all. Expanded lazily loads the sweep's items and lists them as TabRows; a row
 * click opens the Page Detail panel, the row's arrow opens the live page.
 */
export function SweepCard({ sweep, expanded, onToggle, onOpenItem, onRequestDelete, onRestore }: SweepCardProps) {
  const qc = useQueryClient();
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Distinguishes a row's "open live" arrow click from a row-body click.
  const suppress = useRef(false);

  const itemsQ = useQuery({
    queryKey: ['sweep', sweep.id],
    queryFn: () => api.vault.sweep(sweep.id),
    enabled: expanded,
    staleTime: 60_000,
  });

  const items: Item[] = itemsQ.data?.items ?? [];
  const title = fallbackTitle(sweep);
  const browser = deriveBrowser(sweep.deviceId);
  const dot = BROWSER_COLORS[browser];

  const shownCount = Math.min(3, Math.max(1, sweep.tabCount));
  const letters = items.length
    ? items.slice(0, shownCount).map((it) => (domainOf(it.url).charAt(0) || '?').toUpperCase())
    : seedLetters(sweep.id, shownCount);
  const overflow = Math.max(0, sweep.tabCount - letters.length);

  const kindTag = sweep.kind === 'import' ? 'import' : sweep.kind === 'snapshot' ? 'snapshot' : null;

  async function restoreAll(): Promise<void> {
    let list = items;
    if (!list.length) {
      const data = await qc.fetchQuery({
        queryKey: ['sweep', sweep.id],
        queryFn: () => api.vault.sweep(sweep.id),
      });
      list = data.items;
    }
    for (const it of list) window.open(it.url, '_blank', 'noopener,noreferrer');
  }

  const visible = showAll ? items : items.slice(0, PREVIEW);
  const remaining = sweep.tabCount - visible.length;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        zIndex: menuOpen ? 30 : undefined,
        background: 'var(--surface-1)',
        border: `1px solid ${hover || expanded ? 'var(--line-strong)' : 'var(--line)'}`,
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--shadow-card)',
        padding: 16,
        color: 'var(--text-1)',
      }}
    >
      {/* header (click toggles expand) */}
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <FaviconStack letters={letters} overflow={overflow} size={22} />

        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </span>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
            }}
          >
            <span>{sweep.tabCount} TABS</span>
            <span>·</span>
            <i style={{ width: 5, height: 5, borderRadius: '50%', background: dot }} />
            <span>{browser}</span>
            <span>·</span>
            <span>{formatTime(sweep.clientCreatedAt)}</span>
          </span>
        </span>

        {kindTag && (
          <span style={{ flexShrink: 0 }}>
            <Tag tone="neutral">{kindTag}</Tag>
          </span>
        )}

        <span
          onClick={(e) => {
            e.stopPropagation();
            if (onRestore) onRestore();
            else void restoreAll();
          }}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--primary)',
            border: '1px solid var(--line-primary)',
            borderRadius: 'var(--r-pill)',
            padding: '7px 14px',
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
        >
          Restore all
        </span>

        <span
          aria-label={expanded ? 'Collapse session' : 'Expand session'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: 9,
            border: '1px solid var(--line-strong)',
            color: 'var(--text-2)',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        >
          <Icon name="chevron-down" size={12} />
        </span>

        <span style={{ position: 'relative' }}>
          <span
            aria-label="Session actions"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 9,
              color: 'var(--text-3)',
              letterSpacing: 1,
              fontWeight: 700,
              cursor: 'pointer',
              background: menuOpen ? 'var(--surface-2)' : 'transparent',
            }}
          >
            ⋯
          </span>
          {menuOpen && (
            <>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                }}
                style={{ position: 'fixed', inset: 0, zIndex: 30 }}
              />
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 6px)',
                  minWidth: 168,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--line-strong)',
                  borderRadius: 'var(--r-md)',
                  boxShadow: 'var(--shadow-pop)',
                  padding: 6,
                  zIndex: 31,
                }}
              >
                <span
                  onClick={() => {
                    setMenuOpen(false);
                    if (onRestore) onRestore();
                    else void restoreAll();
                  }}
                  style={menuItemStyle('var(--text-1)')}
                >
                  <Icon name="external" size={13} />
                  Open all tabs
                </span>
                <span
                  onClick={() => {
                    setMenuOpen(false);
                    onRequestDelete();
                  }}
                  style={menuItemStyle('var(--danger)')}
                >
                  <Icon name="trash" size={13} />
                  Delete session
                </span>
              </div>
            </>
          )}
        </span>
      </div>

      {/* expanded tab list */}
      {expanded && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 6 }}>
          {itemsQ.isPending && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '6px 8px' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                  <Skeleton width={18} height={18} radius={5} />
                  <Skeleton width="45%" height={12} />
                  <Skeleton width={90} height={10} style={{ marginLeft: 'auto' }} />
                </div>
              ))}
            </div>
          )}

          {itemsQ.isError && (
            <div style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 10 }}>
              Couldn’t load this session’s tabs.
              <span onClick={() => void itemsQ.refetch()} style={{ color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}>
                Try again
              </span>
            </div>
          )}

          {!itemsQ.isPending && !itemsQ.isError && visible.length === 0 && (
            <div style={{ padding: '10px 8px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-3)' }}>
              No tabs left in this session.
            </div>
          )}

          {visible.map((it, i) => (
            <div
              key={it.id}
              onClick={() => {
                if (suppress.current) {
                  suppress.current = false;
                  return;
                }
                onOpenItem(it);
              }}
              style={{ cursor: 'pointer' }}
            >
              <TabRow
                letter={(domainOf(it.url).charAt(0) || '?').toUpperCase()}
                colorIndex={i}
                title={it.title || domainOf(it.url)}
                domain={domainOf(it.url)}
                time={formatTime(it.updatedAt)}
                onRestore={() => {
                  suppress.current = true;
                  window.open(it.url, '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          ))}

          {remaining > 0 && (
            <span
              onClick={() => setShowAll(true)}
              style={{ display: 'block', textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--primary)', padding: '8px 0 2px', cursor: 'pointer' }}
            >
              Show {remaining} more {remaining === 1 ? 'tab' : 'tabs'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function menuItemStyle(color: string): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '8px 10px',
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 500,
    color,
    cursor: 'pointer',
  };
}
